import { CircularLoader } from '@/components/ui/CircularLoader';
import { MessageCircle } from '@/components/ui';
import { useI18n } from '@/context/I18nProvider';
import { ConversationPreview } from '@/services/types/Lead.types';
import { forwardRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { ConversationItem } from '../conversation-chat/ConversationItem';

type ConversationsListProps = {
  conversations: ConversationPreview[];
  selectedConversation: string;
  onSelectConversation: (id: string) => void;
  fetchNextPage: () => void;
  hasMore: boolean;
  isMobile?: boolean;
  isLoading?: boolean;
  isSearching?: boolean;
};

const ConversationsList = forwardRef<HTMLDivElement, ConversationsListProps>(
  (
    {
      conversations,
      selectedConversation,
      onSelectConversation,
      fetchNextPage,
      hasMore,
      isMobile,
      isLoading,
      isSearching,
    },
    ref
  ) => {
    const { t } = useI18n();

    if (conversations.length === 0) {
      if (isLoading) {
        return (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <CircularLoader />
          </div>
        );
      }

      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground pointer-events-none">
          <MessageCircle className="size-10 stroke-1" />
          <p className="text-sm">
            {isSearching ? t('conversations.noSearchResults') : t('conversations.noConversations')}
          </p>
        </div>
      );
    }

    return (
      <div ref={ref} id="conversation-scroll-container" className="overflow-y-auto h-full px-0.5">
        <InfiniteScroll
          dataLength={conversations.length}
          next={fetchNextPage}
          hasMore={hasMore}
          scrollThreshold={0.9}
          loader={
            <div className="flex justify-center py-4">
              <CircularLoader />
            </div>
          }
          scrollableTarget="conversation-scroll-container"
        >
          {conversations.map((conversation) => (
            <ConversationItem
              key={conversation.LeadID}
              id={conversation.LeadID}
              channel={conversation.Channel}
              name={`${conversation.FirstName}${conversation.LastName ? ` ${conversation.LastName}` : ''}`}
              date={conversation.LatestMessage?.Timestamp ?? ''}
              message={conversation.LatestMessage?.Content ?? ''}
              isActive={selectedConversation === conversation.LeadID}
              onClick={() => onSelectConversation(conversation.LeadID)}
              profilePictureURL={conversation.ProfilePictureURL}
              businessWhatsappPhoneNumber={conversation.BusinessWhatsappPhoneNumber}
              businessInstagramUsername={conversation.BusinessInstagramUsername}
              isMobile={isMobile}
            />
          ))}
        </InfiniteScroll>
      </div>
    );
  }
);

ConversationsList.displayName = 'ConversationsList';

export { ConversationsList };
